import React, { useCallback } from 'react';
import type { NativeSyntheticEvent } from 'react-native';
import { Modal as GarnishModal } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

/**
 * Wrapper around garnish modal to hook into telemetry.
 * Also to provide the backdrop a11y label localization.
 * And finally to ensure the accessibility label for the modal is present.
 */

// @ts-expect-error: Modal is missing compound components that are added below.
export const Modal: typeof GarnishModal = (props: ModalProps) => {
  const { onRequestClose } = props;

  // ─── Localization ────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const backdropA11yLabel = t('general.modal.backdrop.a11y-label');

  // ─── Telemetry ───────────────────────────────────────────────────────

  const { track } = useTelemetry();

  const handleClose = useCallback(
    (event: NativeSyntheticEvent<Event>) => {
      track('modal.close');
      onRequestClose?.(event);
    },
    [track, onRequestClose],
  );

  return (
    <GarnishModal
      backdropAccessibilityLabel={backdropA11yLabel}
      {...props}
      onRequestClose={handleClose}
    />
  );
};

// For the compound components.
// eslint-disable-next-line functional/immutable-data
Object.assign(Modal, GarnishModal);

// ─── Types ─────────────────────────────────────────────────────────────
// This setup allows the current Modal component to be used as normal.
// But new implementations with this component will require the a11y label.

type ModalProps = React.ComponentProps<typeof GarnishModal> & {
  readonly accessibilityLabel: string;
};
